import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithElementSearchAndFilters,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { GROUP_BY_KEY } from '@blaze-shared/datasets-domain';
import { DEFAULT_NO_DATA_MESSAGE, SearchAndFiltersMode } from '@blaze-shared/elements-domain';
import { DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';

import { TableDefinitionMapper } from './ai/table.definition-mapper';
import { TableElementHandler } from './ai/table.element-handler';
import { tableV2Migration } from './migrations/002-table.migration';
import { tableV3Migration } from './migrations/003-table.migration';
import { tableV4Migration } from './migrations/004-table.migration';
import { TableComponent } from './table.component';
import { TablePropsBuilder } from './table.props-builder';
import { blankFormat, DEFAULT_HEIGHT, DEFAULT_WIDTH, getPropsConfig } from './table.props-config';
import { TablePropsExtractor } from './table.props-extractor';
import { type TablePropsModel } from './table.props-model';

export type TableElement = CanvasElementFrom<'table'>;
export type TableDef = WithProps<TablePropsModel> & WithElementSearchAndFilters & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    table: TableDef;
  }
}

registerElementDefinition({
  type: 'table',
  componentType: TableComponent,
  isListed: true,
  name: 'Table',
  width: { mode: 'full', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsBuilder: TablePropsBuilder,
  propsExtractor: TablePropsExtractor,
  defaults: getDefaults(),
  initial: getInitial(),
  tags: ['List'],
  styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],
  metaUiFeatures: {
    type: 'container',
    paths: {
      columns: 'content.columns',
    },
  },
  features: {
    addRelatedWidgetSupport: true,
    loadingIndication: 'fadeWithIndicator',
    preventAutoWidth: true,
    visualStyle: 'block',
    ai: {
      showAiPromptAutomatically: true,
      description: true,
    },
  },
  ai: {
    name: 'table',
    handler: () => TableElementHandler,
    definitionMapper: () => TableDefinitionMapper,
  },
  version: 4,
  migrations: {
    2: tableV2Migration,
    3: tableV3Migration,
    4: tableV4Migration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        isQueryable: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      { eventType: 'row-select', schema: DEFAULT_SCHEMA },
      { eventType: 'query', schema: null },
      { eventType: 'action', schema: DEFAULT_SCHEMA },
    ],
  };
}

function getDefaults(): PropsOf<'table'> {
  return {
    content: {
      actions: {
        disableActionWhenPending: true,
      },
      title: {
        label: 'Table',
        align: 'left',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },

      itemAdd: { enabled: false, label: '' },
      itemEdit: { enabled: false },
      itemDelete: { enabled: false },
      itemView: { enabled: false },
      bulkExport: { enabled: false },
      bulkDelete: { enabled: false },
      bulkEdit: { enabled: false },
      table: {
        actionsDisplayMode: 'always',
        actionsView: 'row',
        alwaysVisibleActions: false,
        alwaysVisibleColumnsCount: 1,
        alwaysVisibleFirst: false,
        autoSelectFirstRow: true,
        bulkActionsDisplayMode: 'always',
        disableUserSort: false,
        editable: false,
        emptyTableLabel: DEFAULT_NO_DATA_MESSAGE,
        footerAlwaysVisible: false,
        groupBy: [],
        groupByColumn: {
          align: 'left',
          format: 'text',
          isGroupBy: true,
          label: 'GroupBy',
          name: GROUP_BY_KEY,
        },
        groupByStartWithCollapsed: false,
        headerAlwaysVisible: false,
        origin: 'default',
        showColumnHeaders: true,
        singleSelect: true,
        usePagination: true,
      },
      sort: {
        field: '',
        direction: '',
      },
      pagination: {
        enabled: true,
        count: 10,
        server: false,
      },
      columns: [],
      rowActions: [],
      bulkActions: [],
      previewData: [],
      unifiedRowActions: [],
      unifiedBulkActions: [],
      unifiedOtherActions: [],

      searchAndFilters: {
        mode: SearchAndFiltersMode.PREDEFINED,
        filters: [],
      },
      filterBuilderConfig: {},
      conditionalFormatting: {
        enabled: false,
        formats: [blankFormat()],
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      header: {
        background: { color1: '$t.complementary', selectorType: 'solid' },
        label: { color1: '$t.title', selectorType: 'solid' },
      },
      container: {
        outlineWidth: '$t.containerOutlineWidth',
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
      },
      table: {
        text: { color1: '$t.value', selectorType: 'solid' },
        background: { color1: '$t.fillInternal', selectorType: 'solid' },
        lines: { color1: '$t.outline', selectorType: 'solid' },
        selectedRow: { color1: '$t.accent_a10', selectorType: 'solid' },
        hover: { color1: '$t.complementary', selectorType: 'solid' },
        hideColumnsBorder: true,
        rowHeightBehavior: 'auto',
        rowHeight: 'small',
        rowLineClamp: 1,
        radius: '$t.radius',
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingInternal' },
        group: {
          text: { color1: '$t.value' },
          fontSize: '$t.fontSize',
          fontFamily: '$t.fontFamily',
          fontWeight: '500',
          style: [],
          colorGenOptions: 'generate',
          bgBaseColor: { color1: '#E2E2E2' },
          bgLevel1: { color1: '#FBF8CC' },
          bgLevel2: { color1: '#FDE4CF' },
          bgLevel3: { color1: '#FFCFD2' },
        },
      },
      submenu: {
        value: { color1: '$t.value', selectorType: 'solid' },
        background: { color1: '$t.fill', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
      booleanCol: {
        checkedBackground: { color1: '$t.accent', selectorType: 'solid' },
        checkedMark: { color1: '$t.fill', selectorType: 'solid' },
        uncheckedBackground: { color1: '$t.fill', selectorType: 'solid' },
        uncheckedMark: { color1: '#37373780', selectorType: 'solid' },
      },
    },
  };
}

function getInitial(): PartialPropsOf<'table'> {
  return {
    content: {
      columns: [
        { name: 'name', label: 'Name', format: 'text', align: 'left', visible: true },
        { name: 'email', label: 'Email', format: 'email', align: 'left', visible: true },
        { name: 'sales', label: 'Sales', format: 'currency', align: 'right', visible: true },
      ],
      previewData: [
        { name: 'Albert Flores', email: 'albert@example.com', sales: 15450 },
        { name: 'Annette Black', email: 'annette.black@acme.com', sales: 8470 },
        { name: 'Arlene McCoy', email: 'arlene.m@company.com', sales: 1470 },
        { name: 'Bessie Cooper', email: 'cooper@yes.com', sales: 5400 },
        { name: 'Brooklyn Simmons', email: 'simmons@abcd.com', sales: 5740 },
      ],
      searchAndFilters: { injectToElement: true },
      filterBuilderConfig: {},
      table: {
        alwaysVisibleActions: true,
      },
    },
    style: {
      table: {
        rowHeightBehavior: 'fixed',
        rowHeight: 'small',
        rowLineClamp: 1,
      },
    },
    featuresConfig: {
      fieldsLabelGen: 2,
    },
  };
}
